/**
 * Garminアクティビティタイプのスタイル設定
 *
 * アクティビティタイプに応じたアイコン・色・ボーダー色を一元管理
 * garmin-activity-card.tsx と health/activity-card.tsx で共有
 */

import {
  Bike,
  Dumbbell,
  Footprints,
  Heart,
  type LucideIcon,
  Mountain,
  Snowflake,
  TrendingUp,
  Waves,
} from 'lucide-react'

export type GarminActivityStyle = {
  icon: LucideIcon
  /** アイコンの色（Tailwind text-* クラス） */
  color: string
  /** 背景色（Tailwind bg-* クラス） */
  bgColor: string
  /** ボーダー色（Tailwind border-l-* クラス） */
  borderColor: string
}

/** 完全一致用のマッピング */
const EXACT_MATCH_MAP: Record<string, GarminActivityStyle> = {
  running: {
    icon: Footprints,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-l-orange-500',
  },
  walking: { icon: Footprints, color: 'text-green-500', bgColor: 'bg-green-500/10', borderColor: 'border-l-green-500' },
  cycling: { icon: Bike, color: 'text-blue-500', bgColor: 'bg-blue-500/10', borderColor: 'border-l-blue-500' },
  swimming: { icon: Waves, color: 'text-cyan-500', bgColor: 'bg-cyan-500/10', borderColor: 'border-l-cyan-500' },
  hiking: { icon: Mountain, color: 'text-amber-600', bgColor: 'bg-amber-600/10', borderColor: 'border-l-amber-600' },
  resort_snowboarding: {
    icon: Snowflake,
    color: 'text-sky-500',
    bgColor: 'bg-sky-500/10',
    borderColor: 'border-l-sky-500',
  },
  resort_skiing: { icon: Snowflake, color: 'text-sky-500', bgColor: 'bg-sky-500/10', borderColor: 'border-l-sky-500' },
}

/** 部分一致用のルール（garminのタイプ名は "street_running" などの複合形式があるため） */
const PARTIAL_MATCH_RULES: { pattern: string; style: GarminActivityStyle }[] = [
  { pattern: 'run', style: EXACT_MATCH_MAP.running },
  { pattern: 'walk', style: EXACT_MATCH_MAP.walking },
  { pattern: 'cycling', style: EXACT_MATCH_MAP.cycling },
  { pattern: 'bike', style: EXACT_MATCH_MAP.cycling },
  { pattern: 'swim', style: EXACT_MATCH_MAP.swimming },
  { pattern: 'hik', style: EXACT_MATCH_MAP.hiking },
  { pattern: 'climb', style: EXACT_MATCH_MAP.hiking },
  { pattern: 'snow', style: EXACT_MATCH_MAP.resort_snowboarding },
  { pattern: 'ski', style: EXACT_MATCH_MAP.resort_skiing },
  {
    pattern: 'strength',
    style: {
      icon: Dumbbell,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-l-purple-500',
    },
  },
  {
    pattern: 'weight',
    style: {
      icon: Dumbbell,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-l-purple-500',
    },
  },
  {
    pattern: 'cardio',
    style: { icon: Heart, color: 'text-red-500', bgColor: 'bg-red-500/10', borderColor: 'border-l-red-500' },
  },
]

const DEFAULT_STYLE: GarminActivityStyle = {
  icon: TrendingUp,
  color: 'text-teal-500',
  bgColor: 'bg-teal-500/10',
  borderColor: 'border-l-teal-500',
}

/**
 * Garminアクティビティタイプからスタイル情報を取得
 *
 * 完全一致 → 部分一致（includes） → デフォルトの優先順
 */
export const getGarminActivityStyle = (type: string): GarminActivityStyle => {
  // 完全一致を先にチェック
  if (type in EXACT_MATCH_MAP) {
    return EXACT_MATCH_MAP[type]
  }

  // 部分一致（"street_running" → "run" 等）
  const lowerType = type.toLowerCase()
  for (const rule of PARTIAL_MATCH_RULES) {
    if (lowerType.includes(rule.pattern)) {
      return rule.style
    }
  }

  return DEFAULT_STYLE
}
